import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPublicPostBySlug } from "../services/api/blog";
import { BlogPost } from "../types";
import { BASE_URL } from "../services/api/client";
import {
  Calendar,
  User,
  Eye,
  Tag,
  Loader2,
  Clock,
  BookOpen,
  ArrowLeft,
  Share2,
  Facebook,
  Twitter,
  Copy,
  Check,
  MessageCircle,
} from "lucide-react";

const VIEWED_POSTS_KEY = "giz_viewed_posts";

const getViewedPosts = (): string[] => {
  try {
    const stored = localStorage.getItem(VIEWED_POSTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const markPostAsViewed = (postId: string): void => {
  try {
    const viewed = getViewedPosts();
    if (!viewed.includes(postId)) {
      viewed.push(postId);
      localStorage.setItem(VIEWED_POSTS_KEY, JSON.stringify(viewed));
    }
  } catch {}
};

const hasViewedPost = (postId: string): boolean => {
  return getViewedPosts().includes(postId);
};

const calculateReadTime = (content: string | undefined): number => {
  if (!content) return 1;
  const text = content.replace(/<[^>]*>?/gm, "");
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200);
};

const countWords = (content: string | undefined): number => {
  if (!content) return 0;
  const text = content.replace(/<[^>]*>?/gm, "");
  return text.split(/\s+/).filter((word) => word.length > 0).length;
};

export const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setError("Artikel tidak ditemukan");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const tempId = slug;
        const alreadyViewed = hasViewedPost(tempId);
        const response = await getPublicPostBySlug(slug, alreadyViewed);
        const apiData = response.data;

        const mappedPost: BlogPost = {
          id: apiData.id,
          title: apiData.title,
          slug: apiData.slug,
          excerpt: apiData.excerpt || "",
          category: apiData.categories?.[0]?.name || "Umum",
          categories: apiData.categories || [],
          tags: apiData.tags || [],
          medias: apiData.medias || [],
          content: apiData.content || "",
          seoTitle: apiData.seoTitle || apiData.title,
          seoDescription:
            apiData.seoDescription || apiData.metaDescription || "",
          seoKeywords: apiData.seoKeywords || apiData.keywords || "",
          status: (apiData.status === "published"
            ? "Published"
            : "Draft") as any,
          date: apiData.publishedAt || "",
          views: (apiData.postView as any)?.view || 0,
          author: apiData.author?.fullName || "Admin",
        };

        setPost(mappedPost);

        if (!alreadyViewed) {
          markPostAsViewed(apiData.id);
        }

        if (mappedPost.seoTitle) {
          document.title = `${mappedPost.seoTitle} - GIZ Park`;
        }
      } catch (err) {
        console.error("Failed to fetch post:", err);
        setError("Artikel tidak ditemukan atau terjadi kesalahan");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const handleShare = async (
    platform: "facebook" | "twitter" | "whatsapp" | "copy"
  ) => {
    const url = window.location.href;
    const title = post?.title || "";

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`,
          "_blank"
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(title)}`,
          "_blank"
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`,
          "_blank"
        );
        break;
      case "copy":
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {}
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-giz-primary" />
          <p className="text-gray-500 font-medium">Memuat artikel...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Artikel Tidak Ditemukan
          </h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate("/news")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-giz-primary text-white rounded-xl font-bold hover:bg-giz-primary/90 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali ke Berita
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-16 min-h-screen bg-gray-50">
      {/* Hero Image */}
      {post.medias && post.medias.length > 0 && (
        <div className="w-full h-[40vh] md:h-[50vh] relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50 z-10" />
          <img
            src={`${BASE_URL}${post.medias[0].fileUrl}`}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-20">
        {/* Back Button */}
        <button
          onClick={() => navigate("/news")}
          className="mb-6 inline-flex items-center gap-2 text-gray-500 hover:text-giz-primary transition-colors font-medium text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Berita
        </button>

        {/* Article Card */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="p-6 md:p-10 border-b border-gray-100">
            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-4">
              {post.categories.map((cat) => (
                <span
                  key={cat.id}
                  className="bg-giz-primary text-white text-xs font-bold px-3 py-1 rounded-full"
                >
                  {cat.name}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-6">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author || "Admin GIZ"}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString("id-ID", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {post.views} views
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {calculateReadTime(post.content)} menit baca
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-10">
            <div
              className="prose prose-lg prose-indigo max-w-none text-gray-700
                prose-headings:font-bold prose-headings:text-gray-900
                prose-a:text-giz-primary prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-2xl prose-img:shadow-lg
                prose-blockquote:border-l-giz-primary prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-xl"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Stats Info */}
            <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-giz-primary" />
                  <span>{countWords(post.content)} kata</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-giz-primary" />
                  <span>{calculateReadTime(post.content)} menit baca</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-giz-primary" />
                  <span>{post.views} kali dibaca</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1"
                    >
                      <Tag className="w-3 h-3" />
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-gray-500 flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Bagikan:
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleShare("facebook")}
                    className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    title="Bagikan ke Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShare("twitter")}
                    className="p-2 bg-sky-500 text-white rounded-xl hover:bg-sky-600 transition-colors"
                    title="Bagikan ke Twitter"
                  >
                    <Twitter className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShare("whatsapp")}
                    className="p-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors"
                    title="Bagikan ke WhatsApp"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShare("copy")}
                    className="p-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors flex items-center gap-1"
                    title="Salin Link"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-bold text-emerald-600">
                          Tersalin!
                        </span>
                      </>
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate("/news")}
            className="inline-flex items-center gap-2 px-8 py-4 bg-giz-primary text-white rounded-2xl font-bold hover:bg-giz-primary/90 transition-colors shadow-lg shadow-giz-primary/20"
          >
            <ArrowLeft className="w-5 h-5" /> Lihat Berita Lainnya
          </button>
        </div>
      </div>
    </div>
  );
};
